import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const REASONS = { unknown_sku: "unknown SKU", no_price: "no new price or units per pack", not_approved: "not approved", unchanged: "unchanged", listed_twice: "SKU listed twice with different values" };

export const isPriceFile = (text) => {
  const head = text.replace(/^\ufeff/, "").split(/\r?\n/)[0].toLowerCase().split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  return head.includes("sku") && (head.includes("new_cost_price") || head.includes("units_per_pack"));
};

// Preview + confirm for CSV cost-price updates of existing products (by SKU).
export function PriceUpdateDialog({ preview, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);
  if (!preview) return null;
  const { data, text, file } = preview;
  const skipped = Object.entries(data.skipped).filter(([, n]) => n > 0);
  const apply = async () => {
    setBusy(true);
    try {
      const { data: r } = data.updates ? await api.post("/products/import-prices", { csv_text: text, dry_run: false }) : { data: { updates: 0 } };
      let created = 0;
      if (data.new_rows) { const { data: c } = await api.post("/products/import", { csv_text: text, new_only: true }); created = c.created; }
      toast.success(`Updated ${r.updates} product(s)${data.new_rows ? `, added ${created} new product(s)` : ""}`);
      onApplied();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="price-update-dialog">
        <DialogHeader>
          <DialogTitle>Update products from {file}</DialogTitle>
          <DialogDescription>Updates cost prices and/or units per pack of existing products by SKU; cash and corporate prices follow your markups. Nothing is saved until you confirm.</DialogDescription>
        </DialogHeader>
        <div className="text-sm space-y-1" data-testid="price-update-summary">
          <div><b data-testid="price-update-count">{data.updates}</b> product(s) will be updated, <b>{Object.values(data.skipped).reduce((a, b) => a + b, 0)}</b> skipped</div>
          {skipped.length > 0 && <div className="text-xs text-muted-foreground">{skipped.map(([k, n]) => `${n} ${REASONS[k]}`).join(" · ")}</div>}
          {data.new_rows > 0 && <div data-testid="price-update-new-rows"><b>{data.new_rows}</b> row(s) without an SKU will be added as new products (existing products are skipped)</div>}
        </div>
        {data.sample.length > 0 && (
          <div className="max-h-72 overflow-auto rounded border text-xs">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Product</th><th className="text-right p-2">Old cost</th><th className="text-right p-2">New cost</th></tr></thead>
              <tbody>{data.sample.map((u) => (
                <tr key={u.sku} className="border-t" data-testid={`price-update-row-${u.sku}`}>
                  <td className="p-2">{u.name} <span className="font-mono text-muted-foreground">{u.sku}</span>
                    {u.upp_changed && <div className="text-muted-foreground" data-testid={`price-update-upp-${u.sku}`}>Units per pack: {u.old_upp || "—"} → {u.new_upp}</div>}</td>
                  <td className="p-2 text-right font-mono">{fmtGHS(u.old)}</td><td className="p-2 text-right font-mono">{u.price_changed ? fmtGHS(u.new) : "unchanged"}</td>
                </tr>))}</tbody>
            </table>
            {data.updates > data.sample.length && <div className="p-2 text-muted-foreground">Showing the {data.sample.length} biggest changes of {data.updates}.</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="price-update-cancel">Cancel</Button>
          <Button disabled={busy || (!data.updates && !data.new_rows)} onClick={apply} data-testid="price-update-confirm">{busy ? "Updating…" : "Confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
